import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import moment from 'moment';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import AnalyticsInterval from '../../../../../shared/models/analytics/AnalyticsInterval';
import {
    color_shades_dark,
    color_text_dashboard_muted,
    color_shades_darker,
    color_shades_darkest,
    color_shades_light,
    color_shades_lighter,
    color_defaults_white,
    voicify_spark_darkest,
    voicify_spark_darker,
    voicify_spark_dark,
    voicify_spark_light,
    voicify_spark_lightest
} from '../../../../../shared/constants/colors';

const calendarIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' /%3E%3C/svg%3E`;
const chevronDownIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E`;

interface DashboardDatePickerProps {
    startDate: string;
    endDate: string;
    interval: AnalyticsInterval;
    onChange: (startDate: string, endDate: string, interval: AnalyticsInterval) => void;
    isDisabled?: boolean;
    enableTimeSelection?: boolean;
    mode?: 'dropdown' | 'modal';
    isOpen?: boolean;
    onRequestClose?: () => void;
    allowDismiss?: boolean;
    modalTitle?: string;
    modalDescription?: string;
    applyButtonLabel?: string;
}

type QuickSelectOption = 'today' | 'yesterday' | 'past7days' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';

const DATE_FORMAT = "YYYY-MM-DD";
const DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSS";
const TIME_FORMAT = "HH:mm";

const getPresetQuickSelectDates = (option: Exclude<QuickSelectOption, 'custom'>): { start: moment.Moment; end: moment.Moment; interval: AnalyticsInterval } => {
    const now = moment();
    const today = now.clone().startOf('day');

    switch (option) {
        case 'today':
            return { start: today.clone(), end: today.clone(), interval: 'day' };
        case 'yesterday':
            return { start: now.clone().subtract(1, 'day').startOf('day'), end: now.clone().subtract(1, 'day').startOf('day'), interval: 'day' };
        case 'past7days':
            return { start: now.clone().subtract(6, 'days').startOf('day'), end: today.clone(), interval: 'past 7 days' };
        case 'thisWeek':
            return { start: now.clone().startOf('week'), end: today.clone(), interval: 'week' };
        case 'lastWeek':
            return { start: now.clone().subtract(1, 'week').startOf('week'), end: now.clone().subtract(1, 'week').endOf('week'), interval: 'week' };
        case 'thisMonth':
            return { start: now.clone().startOf('month'), end: today.clone(), interval: 'this month' };
        case 'lastMonth':
            return { start: now.clone().subtract(1, 'month').startOf('month'), end: now.clone().subtract(1, 'month').endOf('month'), interval: 'past month' };
        case 'thisYear':
            return { start: now.clone().startOf('year'), end: today.clone(), interval: 'year' };
    }
};

const quickSelectOptions: { key: Exclude<QuickSelectOption, 'custom'>; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'past7days', label: 'Past 7 Days' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'lastWeek', label: 'Last Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'thisYear', label: 'This Year' }
];

const DashboardDatePicker: React.FC<DashboardDatePickerProps> = ({
    startDate,
    endDate,
    interval,
    onChange,
    isDisabled = false,
    enableTimeSelection = false,
    mode = 'dropdown',
    isOpen = false,
    onRequestClose,
    allowDismiss = true,
    modalTitle,
    modalDescription,
    applyButtonLabel = 'Apply Range'
}) => {
    const isModalMode = mode === 'modal';
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedOption, setSelectedOption] = useState<QuickSelectOption>('custom');
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const [tempInterval, setTempInterval] = useState<AnalyticsInterval>(interval);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(400);
    const [isDropdownCentered, setIsDropdownCentered] = useState(false);
    const [centeredPanelWidth, setCenteredPanelWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const dateButtonRef = useRef<HTMLButtonElement>(null);
    const wasDropdownCenteredRef = useRef(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const panelContentRef = useRef<HTMLDivElement>(null);
    const isPickerOpen = isModalMode ? isOpen : isDropdownOpen;

    // Sync temp dates when props change
    useEffect(() => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        setTempInterval(interval);

        const parsedStartDate = safeParseDate(startDate, moment.invalid());
        const parsedEndDate = safeParseDate(endDate, moment.invalid());

        if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
            setSelectedOption('custom');
            return;
        }

        if (enableTimeSelection && (hasCustomTime(startDate, 'start') || hasCustomTime(endDate, 'end'))) {
            setSelectedOption('custom');
            return;
        }

        const matchingOption = quickSelectOptions.find(option => {
            const preset = getPresetQuickSelectDates(option.key);
            return parsedStartDate.isSame(preset.start, 'day') && parsedEndDate.isSame(preset.end, 'day');
        });

        setSelectedOption(matchingOption?.key || 'custom');
    }, [startDate, endDate, interval, enableTimeSelection]);

    // Measure available space below the button when opening
    useEffect(() => {
        if (!isModalMode && isPickerOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom - 12;
            setDropdownMaxHeight(Math.max(200, spaceBelow));
        }

        if (!isPickerOpen) {
            setIsDropdownCentered(false);
        }
    }, [isModalMode, isPickerOpen]);

    // On short screens the dropdown can't fit below the button and would need to scroll,
    // so move it to the center of the viewport where it has the full screen height
    useLayoutEffect(() => {
        if (isModalMode || !isPickerOpen || isDropdownCentered
            || !panelRef.current || !panelContentRef.current || !containerRef.current) return;

        // Compare the panel's full height against the current space below the button,
        // not against dropdownMaxHeight, which may still hold a previous opening's value
        const content = panelContentRef.current;
        const requiredHeight = panelRef.current.offsetHeight + (content.scrollHeight - content.clientHeight);
        const spaceBelow = window.innerHeight - containerRef.current.getBoundingClientRect().bottom - 12;
        if (requiredHeight > spaceBelow + 1) {
            setCenteredPanelWidth(containerRef.current.offsetWidth);
            setIsDropdownCentered(true);
        }
    });

    // Move focus into the centered panel, and back to the date button once it closes
    useEffect(() => {
        if (isDropdownCentered) {
            wasDropdownCenteredRef.current = true;
            panelRef.current?.focus();
        } else if (wasDropdownCenteredRef.current) {
            wasDropdownCenteredRef.current = false;
            dateButtonRef.current?.focus();
        }
    }, [isDropdownCentered]);

    // Keep keyboard focus inside the centered panel while its backdrop is shown
    const handleCenteredPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            setIsDropdownOpen(false);
            return;
        }

        if (event.key !== 'Tab' || !panelRef.current) return;

        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (containerRef.current && !containerRef.current.contains(target)
                && !(panelRef.current && panelRef.current.contains(target))) {
                setIsDropdownOpen(false);
            }
        };

        if (!isModalMode && isPickerOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isModalMode, isPickerOpen]);

    useEffect(() => {
        if (!isModalMode || !isPickerOpen) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isModalMode, isPickerOpen]);

    useEffect(() => {
        if (!isModalMode || !isPickerOpen || !allowDismiss) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onRequestClose?.();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [allowDismiss, isModalMode, isPickerOpen, onRequestClose]);

    // Helper to safely parse a date string, returning a valid moment or a fallback
    const safeParseDate = (dateStr: string, fallback: moment.Moment): moment.Moment => {
        const isoParsed = moment(dateStr, moment.ISO_8601, true);
        if (isoParsed.isValid()) {
            return isoParsed;
        }

        const parsed = moment(dateStr, [DATE_TIME_FORMAT, DATE_FORMAT, 'YYYY-MM-DD HH:mm'], true);
        if (parsed.isValid()) {
            return parsed;
        }
        // Try lenient parsing as a backup
        const lenientParsed = moment(dateStr);
        return lenientParsed.isValid() ? lenientParsed : fallback;
    };

    const isDateOnlyValue = (dateStr: string): boolean => moment(dateStr, DATE_FORMAT, true).isValid();

    const parseDateForBoundary = (dateStr: string, boundary: 'start' | 'end', fallback: moment.Moment): moment.Moment => {
        const parsed = safeParseDate(dateStr, fallback);
        if (!parsed.isValid() || !isDateOnlyValue(dateStr)) {
            return parsed;
        }

        return boundary === 'start' ? parsed.startOf('day') : parsed.endOf('day');
    };

    const hasCustomTime = (dateStr: string, boundary: 'start' | 'end'): boolean => {
        if (isDateOnlyValue(dateStr)) return false;

        const parsed = safeParseDate(dateStr, moment.invalid());
        if (!parsed.isValid()) return false;

        if (boundary === 'start') {
            return parsed.hours() !== 0 || parsed.minutes() !== 0 || parsed.seconds() !== 0 || parsed.milliseconds() !== 0;
        }

        return parsed.hours() !== 23 || parsed.minutes() !== 59 || parsed.seconds() !== 59 || parsed.milliseconds() !== 999;
    };

    const formatDateForValue = (date: moment.Moment, boundary: 'start' | 'end', includeTime: boolean): string => {
        if (!includeTime) {
            return date.format(DATE_FORMAT);
        }

        const normalized = date.clone();
        if (boundary === 'start') {
            normalized.seconds(0).milliseconds(0);
        } else {
            normalized.seconds(59).milliseconds(999);
        }
        return normalized.format(DATE_TIME_FORMAT);
    };

    const updateTimeForDate = (dateStr: string, timeValue: string, boundary: 'start' | 'end'): string => {
        const [hours, minutes] = timeValue.split(':').map(value => parseInt(value, 10));
        const fallback = boundary === 'start' ? moment().startOf('month') : moment().endOf('day');
        const next = safeParseDate(dateStr, fallback).clone();

        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return formatDateForValue(next, boundary, true);
        }

        next.hours(hours).minutes(minutes);
        return formatDateForValue(next, boundary, true);
    };

    const formatSelectedRangeDisplay = () => {
        const start = parseDateForBoundary(tempStartDate, 'start', moment().startOf('month'));
        const end = parseDateForBoundary(tempEndDate, 'end', moment().endOf('day'));
        const showTime = enableTimeSelection && selectedOption === 'custom' && (hasCustomTime(tempStartDate, 'start') || hasCustomTime(tempEndDate, 'end'));
        const startFormat = showTime ? 'MMM D, h:mm A' : 'MMM D';
        const endFormat = showTime ? 'MMM D, YYYY, h:mm A' : 'MMM D, YYYY';

        return `${start.format(startFormat)} → ${end.format(endFormat)}`;
    };

    const formatDateRange = () => {
        const today = moment().startOf('day');
        const startOfMonth = moment().startOf('month');

        const start = safeParseDate(startDate, startOfMonth);
        const end = safeParseDate(endDate, today);

        const showTime = enableTimeSelection && (hasCustomTime(startDate, 'start') || hasCustomTime(endDate, 'end'));

        if (start.isSame(end, 'day')) {
            return showTime ? `${start.format('MMM D, YYYY h:mm A')} - ${end.format('h:mm A')}` : start.format('MMM D, YYYY');
        }

        if (start.isSame(end, 'year')) {
            return showTime ? `${start.format('MMM D, h:mm A')} - ${end.format('MMM D, YYYY h:mm A')}` : `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
        }

        return showTime ? `${start.format('MMM D, YYYY h:mm A')} - ${end.format('MMM D, YYYY h:mm A')}` : `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
    };

    const getQuickSelectDates = (option: QuickSelectOption): { start: moment.Moment; end: moment.Moment; interval: AnalyticsInterval } => {
        if (option === 'custom') {
            const now = moment();
            const today = now.clone().startOf('day');
            return {
                start: parseDateForBoundary(tempStartDate, 'start', now.clone().startOf('month')),
                end: parseDateForBoundary(tempEndDate, 'end', today.clone().endOf('day')),
                interval: 'custom'
            };
        }

        return getPresetQuickSelectDates(option);
    };

    const closePicker = () => {
        if (isModalMode) {
            if (allowDismiss) {
                onRequestClose?.();
            }
            return;
        }

        setIsDropdownOpen(false);
    };

    const handleQuickSelect = (option: QuickSelectOption) => {
        setSelectedOption(option);
        const { start, end, interval } = getQuickSelectDates(option);
        setTempStartDate(start.format(DATE_FORMAT));
        setTempEndDate(end.format(DATE_FORMAT));
        setTempInterval(interval);

        if (!isModalMode && option !== 'custom') {
            onChange(start.format(DATE_FORMAT), end.format(DATE_FORMAT), interval);
            setIsDropdownOpen(false);
        }
    };

    const handleDateRangeChange = (ranges: any) => {
        const existingStart = parseDateForBoundary(tempStartDate, 'start', moment().startOf('month'));
        const existingEnd = parseDateForBoundary(tempEndDate, 'end', moment().endOf('day'));
        const start = moment(ranges.selection?.startDate)
            .hours(enableTimeSelection ? existingStart.hours() : 0)
            .minutes(enableTimeSelection ? existingStart.minutes() : 0)
            .seconds(enableTimeSelection ? existingStart.seconds() : 0)
            .milliseconds(enableTimeSelection ? existingStart.milliseconds() : 0);
        const end = moment(ranges.selection?.endDate)
            .hours(enableTimeSelection ? existingEnd.hours() : 23)
            .minutes(enableTimeSelection ? existingEnd.minutes() : 59)
            .seconds(enableTimeSelection ? existingEnd.seconds() : 59)
            .milliseconds(enableTimeSelection ? existingEnd.milliseconds() : 999);

        setSelectedOption('custom');
        setTempStartDate(formatDateForValue(start, 'start', enableTimeSelection));
        setTempEndDate(formatDateForValue(end, 'end', enableTimeSelection));
        setTempInterval('custom');
    };

    const handleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOption('custom');
        setTempStartDate(updateTimeForDate(tempStartDate, event.target.value, 'start'));
        if (isDateOnlyValue(tempEndDate)) {
            setTempEndDate(formatDateForValue(parseDateForBoundary(tempEndDate, 'end', moment().endOf('day')), 'end', true));
        }
        setTempInterval('custom');
    };

    const handleEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOption('custom');
        if (isDateOnlyValue(tempStartDate)) {
            setTempStartDate(formatDateForValue(parseDateForBoundary(tempStartDate, 'start', moment().startOf('day')), 'start', true));
        }
        setTempEndDate(updateTimeForDate(tempEndDate, event.target.value, 'end'));
        setTempInterval('custom');
    };

    const handleApply = () => {
        const start = parseDateForBoundary(tempStartDate, 'start', moment().startOf('month'));
        const end = parseDateForBoundary(tempEndDate, 'end', moment().endOf('day'));
        const nextEndDate = end.isBefore(start) ? formatDateForValue(start, 'end', enableTimeSelection && selectedOption === 'custom') : tempEndDate;

        if (nextEndDate !== tempEndDate) {
            setTempEndDate(nextEndDate);
        }

        onChange(tempStartDate, nextEndDate, tempInterval);

        if (!isModalMode) {
            setIsDropdownOpen(false);
        }
    };

    const selectionRange = {
        startDate: parseDateForBoundary(tempStartDate, 'start', moment().startOf('month')).toDate(),
        endDate: parseDateForBoundary(tempEndDate, 'end', moment().endOf('day')).toDate(),
        key: 'selection',
        color: voicify_spark_dark
    };

    // Minimum date is January 1, 2026 - no data available before this
    const minDate = new Date(2026, 0, 1);

    // Maximum date is today - don't allow future date selection
    // Using end of today to be inclusive of the current day across all timezones
    const maxDate = moment().endOf('day').toDate();

    const renderPickerPanel = () => (
        <>
            {isModalMode && (modalTitle || modalDescription) && (
                <ModalHeader>
                    {modalTitle && <ModalTitle>{modalTitle}</ModalTitle>}
                    {modalDescription && <ModalDescription>{modalDescription}</ModalDescription>}
                </ModalHeader>
            )}

            <PanelContent ref={panelContentRef}>
                {!showCalendar ? (
                    <>
                        <QuickSelectSection>
                            <SectionTitle>Quick Select</SectionTitle>
                            <QuickSelectGrid>
                                {quickSelectOptions.map(option => (
                                    <QuickSelectButton
                                        key={option.key}
                                        isSelected={selectedOption === option.key}
                                        onClick={() => handleQuickSelect(option.key)}
                                    >
                                        {option.label}
                                    </QuickSelectButton>
                                ))}
                            </QuickSelectGrid>
                        </QuickSelectSection>

                        <Divider />

                        <ToggleSectionButton
                            onClick={() => setShowCalendar(true)}
                        >
                            <ToggleButtonLeft>
                                <ToggleButtonLabel>Custom Range</ToggleButtonLabel>
                                <SelectedRangeDisplay>
                                    {formatSelectedRangeDisplay()}
                                </SelectedRangeDisplay>
                            </ToggleButtonLeft>
                            <ToggleButtonChevron isExpanded={false} />
                        </ToggleSectionButton>
                    </>
                ) : (
                    <>
                        <ToggleSectionButton
                            onClick={() => setShowCalendar(false)}
                        >
                            <ToggleButtonLeft>
                                <ToggleButtonLabel>Quick Select</ToggleButtonLabel>
                            </ToggleButtonLeft>
                            <ToggleButtonChevron isExpanded={true} />
                        </ToggleSectionButton>

                        <Divider />

                        <CalendarSection>
                            <CalendarHeader>
                                <SectionTitle>Select Date Range</SectionTitle>
                                <SelectedRangeDisplay>
                                    {formatSelectedRangeDisplay()}
                                </SelectedRangeDisplay>
                            </CalendarHeader>
                            <CalendarWrapper>
                                <DateRangePicker
                                    ranges={[selectionRange]}
                                    onChange={handleDateRangeChange}
                                    months={1}
                                    direction="horizontal"
                                    rangeColors={[voicify_spark_light]}
                                    minDate={minDate as any}
                                    maxDate={maxDate as any}
                                />
                            </CalendarWrapper>
                            {enableTimeSelection && (
                                <TimeSelectionPanel>
                                    <TimeField>
                                        <TimeLabel>Start Time</TimeLabel>
                                        <TimeInput
                                            type="time"
                                            value={parseDateForBoundary(tempStartDate, 'start', moment().startOf('day')).format(TIME_FORMAT)}
                                            onChange={handleStartTimeChange}
                                        />
                                    </TimeField>
                                    <TimeDivider />
                                    <TimeField>
                                        <TimeLabel>End Time</TimeLabel>
                                        <TimeInput
                                            type="time"
                                            value={parseDateForBoundary(tempEndDate, 'end', moment().endOf('day')).format(TIME_FORMAT)}
                                            onChange={handleEndTimeChange}
                                        />
                                    </TimeField>
                                </TimeSelectionPanel>
                            )}
                        </CalendarSection>
                    </>
                )}
            </PanelContent>

            <PanelFooter $isCentered={!allowDismiss && isModalMode}>
                {(allowDismiss || !isModalMode) && (
                    <CancelButton onClick={closePicker}>
                        Cancel
                    </CancelButton>
                )}
                <ApplyButton onClick={handleApply}>
                    {applyButtonLabel}
                </ApplyButton>
            </PanelFooter>
        </>
    );

    if (isModalMode) {
        if (!isPickerOpen) {
            return null;
        }

        return (
            <ModalBackdrop onClick={allowDismiss ? closePicker : undefined}>
                <ModalPanel onClick={(event) => event.stopPropagation()}>
                    {renderPickerPanel()}
                </ModalPanel>
            </ModalBackdrop>
        );
    }

    return (
        <Container ref={containerRef}>
            <DateButton
                ref={dateButtonRef}
                onClick={() => !isDisabled && setIsDropdownOpen(!isDropdownOpen)}
                isOpen={isPickerOpen}
                disabled={isDisabled}
                $isDisabled={isDisabled}
            >
                <CalendarIcon $isDisabled={isDisabled} />
                <DateText>{formatDateRange()}</DateText>
                {isDisabled ? (
                    <LoadingIndicator>
                        <LoadingDot style={{ animationDelay: '0ms' }} />
                        <LoadingDot style={{ animationDelay: '150ms' }} />
                        <LoadingDot style={{ animationDelay: '300ms' }} />
                    </LoadingIndicator>
                ) : (
                    <ChevronIcon isOpen={isPickerOpen} />
                )}
            </DateButton>

            {isPickerOpen && !isDropdownCentered && (
                <DropdownPanel ref={panelRef} $maxHeight={dropdownMaxHeight}>
                    {renderPickerPanel()}
                </DropdownPanel>
            )}

            {isPickerOpen && isDropdownCentered && createPortal(
                <CenteredDropdownBackdrop>
                    <CenteredDropdownPanel
                        ref={panelRef}
                        $width={centeredPanelWidth}
                        tabIndex={-1}
                        role="dialog"
                        aria-modal="true"
                        onKeyDown={handleCenteredPanelKeyDown}
                    >
                        {renderPickerPanel()}
                    </CenteredDropdownPanel>
                </CenteredDropdownBackdrop>,
                document.body
            )}
        </Container>
    );
};

// Styled Components
const Container = styled.div`
    position: relative;
    width: 100%;
`;

const DateButton = styled.button<{ isOpen: boolean; $isDisabled?: boolean }>`
    width: 100%;
    padding: 14px 16px;
    font-family: inherit;
    background: ${props => props.$isDisabled ? color_shades_lighter : props.isOpen ? voicify_spark_lightest : color_defaults_white};
    border-radius: 12px;
    border: 1px solid ${props => props.$isDisabled ? color_shades_light : props.isOpen ? voicify_spark_light : color_shades_light};
    color: ${props => props.$isDisabled ? color_text_dashboard_muted : voicify_spark_darkest};
    font-size: 15px;
    font-weight: 600;
    cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    gap: 12px;
    opacity: ${props => props.$isDisabled ? 0.7 : 1};
    min-height: 48px;

    &:hover {
        background: ${props => props.$isDisabled ? color_shades_lighter : voicify_spark_lightest};
        border-color: ${props => props.$isDisabled ? color_shades_light : voicify_spark_light};
        box-shadow: ${props => props.$isDisabled ? '0 1px 3px rgba(0, 0, 0, 0.08)' : '0 2px 6px rgba(10, 95, 138, 0.12)'};
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 3px rgba(10, 95, 138, 0.1);
    }

    /* Mac 13" small + Chromebook — reduce oversized date button font */
    @media (min-width: 1024px) and (max-width: 1439.98px) {
        font-size: 12px;
        padding: 10px 14px;
        min-height: 40px;
        gap: 10px;
    }

    @media (max-width: 400px) {
        padding: 12px 14px;
        font-size: 14px;
        gap: 10px;
    }
`;

const CalendarIcon = styled.div<{ $isDisabled?: boolean }>`
    width: 18px;
    height: 18px;
    background-image: url("${calendarIcon}");
    background-size: contain;
    background-repeat: no-repeat;
    flex-shrink: 0;
    filter: ${props => props.$isDisabled ? 'grayscale(100%) brightness(150%)' : 'invert(30%) sepia(90%) saturate(600%) hue-rotate(170deg) brightness(85%)'};
    opacity: ${props => props.$isDisabled ? 0.5 : 0.9};
`;

const DateText = styled.span`
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
    width: 16px;
    height: 16px;
    background-image: url("${chevronDownIcon}");
    background-size: contain;
    background-repeat: no-repeat;
    flex-shrink: 0;
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    filter: invert(30%) sepia(90%) saturate(600%) hue-rotate(170deg) brightness(85%);
    opacity: 0.7;
`;

const LoadingIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0 4px;
`;

const LoadingDot = styled.div`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${color_shades_dark};
    animation: loadingPulse 0.8s ease-in-out infinite;

    @keyframes loadingPulse {
        0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
        }
        40% {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

const DropdownPanel = styled.div<{ $maxHeight: number }>`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: ${color_defaults_white};
    border-radius: 16px;
    box-shadow:
        0 10px 40px rgba(0, 0, 0, 0.15),
        0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    overflow: hidden;
    animation: slideDown 0.2s ease;
    max-height: ${props => props.$maxHeight}px;
    display: flex;
    flex-direction: column;

    /* Mac 13" small (1280–1439) */
    @media (min-width: 1280px) and (max-width: 1439.98px) {
        top: calc(100% + 4px);
    }

    /* Chromebook (1024–1279) */
    @media (min-width: 1024px) and (max-width: 1279.98px) {
        top: calc(100% + 4px);
    }

    @media (max-width: 400px) {
        border-radius: 12px;
        left: -8px;
        right: -8px;
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const CenteredDropdownBackdrop = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(34, 46, 61, 0.32);
    z-index: 1500;
`;

const CenteredDropdownPanel = styled.div<{ $width: number }>`
    width: ${props => props.$width ? `${props.$width}px` : '380px'};
    max-width: 100%;
    max-height: calc(100vh - 32px);
    display: flex;
    flex-direction: column;
    background: ${color_defaults_white};
    border-radius: 16px;
    box-shadow:
        0 10px 40px rgba(0, 0, 0, 0.15),
        0 2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    &:focus {
        outline: none;
    }
`;

const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(34, 46, 61, 0.56);
    z-index: 1500;

    @media (max-width: 480px) {
        padding: 16px;
        align-items: flex-end;
    }
`;

const ModalPanel = styled.div`
    width: min(720px, 100%);
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
    background: ${color_defaults_white};
    border-radius: 20px;
    box-shadow:
        0 24px 60px rgba(0, 0, 0, 0.2),
        0 6px 20px rgba(0, 0, 0, 0.12);
    overflow: hidden;

    @media (max-width: 480px) {
        width: 100%;
        max-height: calc(100vh - 24px);
        border-radius: 18px 18px 0 0;
    }
`;

const ModalHeader = styled.div`
    padding: 20px 24px 12px;
    border-bottom: 1px solid ${color_shades_light};
    background: linear-gradient(180deg, ${voicify_spark_lightest} 0%, ${color_defaults_white} 100%);

    @media (max-width: 480px) {
        padding: 18px 18px 10px;
    }
`;

const ModalTitle = styled.h3`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: ${voicify_spark_darkest};
`;

const ModalDescription = styled.p`
    margin: 8px 0 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${color_shades_darker};
`;

const PanelContent = styled.div`
    padding: 10px 8px 6px;
    background: ${color_defaults_white};
    border-radius: 16px 16px 0 0;
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    min-height: 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        padding: 8px 6px 4px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        padding: 6px 4px 3px;
    }

    @media (max-width: 400px) {
        padding: 12px 12px 6px;
        border-radius: 12px 12px 0 0;
    }
`;

const QuickSelectSection = styled.div`
    margin-bottom: 8px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin-bottom: 6px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin-bottom: 4px;
    }
`;

const SectionTitle = styled.h4`
    font-size: 10px;
    font-weight: 700;
    font-family: inherit;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin: 0 0 10px 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin: 0 0 6px 0;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin: 0 0 4px 0;
    }
`;

const QuickSelectGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        gap: 4px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        gap: 3px;
    }

    @media (max-width: 300px) {
        grid-template-columns: 1fr;
    }
`;

const QuickSelectButton = styled.button<{ isSelected: boolean }>`
    padding: 8px 10px;
    font-family: inherit;
    background: ${props => props.isSelected ? voicify_spark_dark : color_shades_lighter};
    color: ${props => props.isSelected ? color_defaults_white : color_shades_darkest};
    border: 1px solid ${props => props.isSelected ? voicify_spark_dark : color_shades_light};
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;

    &:hover {
        background: ${props => props.isSelected ? voicify_spark_darker : voicify_spark_lightest};
        border-color: ${props => props.isSelected ? voicify_spark_darker : voicify_spark_light};
        transform: translateY(-1px);
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 11px;
        padding: 6px 8px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 10px;
        padding: 5px 7px;
    }
`;

const Divider = styled.div`
    height: 1px;
    background: ${color_shades_light};
    margin: 8px 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin: 5px 0;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin: 4px 0;
    }
`;

const CalendarSection = styled.div``;

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 8px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin-bottom: 5px;
        gap: 5px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin-bottom: 4px;
        gap: 4px;
    }

    @media (max-width: 360px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const ToggleSectionButton = styled.button`
    display: flex;
    justify-content: space-between;
    font-family: inherit;
    align-items: center;
    width: 100%;
    padding: 10px 12px;
    background: ${color_shades_lighter};
    color: ${color_shades_darkest};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    min-height: 44px;

    &:hover {
        background: ${voicify_spark_lightest};
        border-color: ${voicify_spark_light};
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 11px;
        padding: 8px 10px;
        min-height: 38px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 10px;
        padding: 7px 9px;
        min-height: 34px;
    }

    @media (max-width: 360px) {
        padding: 8px 10px;
        font-size: 12px;
    }
`;

const ToggleButtonLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;

    @media (max-width: 360px) {
        gap: 6px;
    }
`;

const ToggleButtonLabel = styled.span`
    color: ${color_shades_darker};
`;

const SelectedRangeDisplay = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${voicify_spark_dark};
    background: ${color_defaults_white};
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid ${voicify_spark_light};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 10px;
        padding: 3px 8px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 9px;
        padding: 2px 6px;
    }

    @media (max-width: 360px) {
        font-size: 11px;
        padding: 3px 8px;
    }
`;

const ToggleButtonChevron = styled.div<{ isExpanded: boolean }>`
    width: 14px;
    height: 14px;
    background-image: url("${chevronDownIcon}");
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.2s ease;
    transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    opacity: 0.6;
    filter: invert(40%);
`;

const CalendarWrapper = styled.div`
    overflow-x: hidden;
    overflow-y: visible;
    width: 100%;
    font-family: Muli, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

    * {
        font-family: inherit;
    }

    /* Default desktop: zoom to fit within 380px sidebar */
    .rdrCalendarWrapper {
        zoom: 0.88;
    }

    /* Mac 13" small (1280–1439): zoom-out to fit 290px panel */
    @media (min-width: 1280px) and (max-width: 1439.98px) {
        .rdrCalendarWrapper {
            zoom: 0.76;
        }
    }

    /* Chromebook (1024–1279): strong zoom-out to fit 260px panel */
    @media (min-width: 1024px) and (max-width: 1279.98px) {
        .rdrCalendarWrapper {
            zoom: 0.62;
        }
    }

    @media (max-width: 360px) {
        .rdrCalendarWrapper {
            zoom: 0.85;
        }
    }

    .rdrMonthAndYearPickers {
        font-weight: 600;
        font-size: 13px;
    }

    .rdrNextPrevButton {
        background: ${color_shades_lighter};
        border-radius: 6px;
        width: 28px;
        height: 28px;
        min-width: 28px;
        
        &:hover {
            background: ${voicify_spark_lightest};
        }
    }

    .rdrDays {
        padding: 0;
    }

    .rdrDay {
        height: 28px;
        line-height: 28px;
    }

    .rdrDayNumber {
        top: 0;
        bottom: 0;
    }

    .rdrDayNumber span {
        color: ${color_shades_darkest};
        font-weight: 500;
        font-size: 12px;
    }

    .rdrDayPassive .rdrDayNumber span {
        color: ${color_text_dashboard_muted};
    }

    .rdrDayToday .rdrDayNumber span:after {
        background: ${voicify_spark_dark};
        bottom: 2px;
    }

    .rdrSelected, .rdrInRange, .rdrStartEdge, .rdrEndEdge {
        background: ${voicify_spark_light};
    }

    .rdrStartEdge, .rdrEndEdge {
        background: ${voicify_spark_dark};
    }

    .rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview {
        border-color: ${voicify_spark_dark};
    }

    .rdrDateDisplayWrapper {
        display: none;
    }

    .rdrDefinedRangesWrapper {
        display: none;
    }
`;

const TimeSelectionPanel = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    padding: 8px 10px;
    background: ${voicify_spark_lightest};
    border: 1px solid ${voicify_spark_light};
    border-radius: 10px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        gap: 6px;
        padding: 6px 8px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        gap: 5px;
        padding: 5px 6px;
    }

    @media (max-width: 360px) {
        grid-template-columns: 1fr;
        gap: 8px;
    }
`;

const TimeField = styled.label`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const TimeLabel = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (min-width: 1024px) and (max-width: 1439.98px) {
        font-size: 8px;
    }
`;

const TimeInput = styled.input`
    width: 100%;
    min-width: 0;
    height: 34px;
    padding: 6px 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    color: ${voicify_spark_darkest};
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 7px;
    outline: none;

    &:focus {
        border-color: ${voicify_spark_dark};
        box-shadow: 0 0 0 2px rgba(10, 95, 138, 0.12);
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        height: 30px;
        font-size: 11px;
        padding: 4px 6px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        height: 28px;
        font-size: 10px;
        padding: 3px 5px;
    }
`;

const TimeDivider = styled.div`
    width: 1px;
    height: 34px;
    background: ${voicify_spark_light};

    @media (max-width: 360px) {
        display: none;
    }
`;

const PanelFooter = styled.div<{ $isCentered?: boolean }>`
    display: flex;
    justify-content: ${props => props.$isCentered ? 'center' : 'flex-end'};
    gap: 10px;
    padding: 8px 12px;
    background: ${color_shades_lighter};
    border-top: 1px solid ${color_shades_light};
    flex-shrink: 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        padding: 5px 8px;
        gap: 6px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        padding: 4px 6px;
        gap: 5px;
    }

    @media (max-width: 360px) {
        padding: 10px 12px;
        gap: 8px;
    }
`;

const CancelButton = styled.button`
    padding: 8px 16px;
    font-family: inherit;
    background: ${color_defaults_white};
    color: ${color_shades_darker};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    min-height: 36px;

    &:hover {
        background: ${color_shades_lighter};
        border-color: ${color_shades_dark};
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 10px;
        padding: 8px 12px;
        min-height: 40px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 9px;
        padding: 8px 10px;
        min-height: 40px;
    }

    @media (max-width: 360px) {
        padding: 6px 12px;
        font-size: 12px;
    }
`;

const ApplyButton = styled.button`
    padding: 8px 16px;
    font-family: inherit;
    background: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: 0 1px 3px rgba(10, 95, 138, 0.2);
    min-height: 36px;

    &:hover {
        background: ${voicify_spark_dark};
        box-shadow: 0 2px 6px rgba(10, 95, 138, 0.25);
    }

    &:active {
        transform: translateY(0);
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 10px;
        padding: 8px 12px;
        min-height: 40px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 9px;
        padding: 8px 10px;
        min-height: 40px;
    }

    @media (max-width: 360px) {
        padding: 6px 12px;
        font-size: 12px;
    }
`;

export default DashboardDatePicker;
